import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { createRootAndRender, unmount } from '../../../utils/react.tsx';
import { ModalBackButton, ModalBackButtonProps } from './modal-back-button.tsx';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { classList } from '../../../utils/string.ts';
import composeRefs from '../../../utils/compose-refs.ts';
import { Icon } from '../../00-atoms/icon/icon.tsx';
import { isMobile } from '../../../utils/user-agent/user-agent.tsx';
import { useDismissViaEscape, useModalPopStateInterceptor, useOnClose } from './modal-utils.tsx';
import { useFocusTrap } from '../../../utils/hooks/use-focus-trap.tsx';
import { isPrimaryMouseButtonWithoutModifier } from '../../../utils/mouse.ts';
import { BackdropScrollContainer } from '../../00-atoms/backdrop-scroll-container/backdrop-scroll-container.tsx';
import { Backdrop } from '../../00-atoms/backdrop/backdrop.tsx';
import { ariaDialogPattern } from '../../../utils/aria.tsx';
import { useIsSsr } from '../../../utils/hooks/use-is-ssr.tsx';
import { Cookie } from '../../../utils/cookie.ts';

/**
 * See aria dialog description for usage: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
const modalAriaLabelledById = 'modal-label';
const modalAriaDescribedById = 'modal-description';

/**
 * A modal's headline should be wrapped with this to support aria.
 * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
export const ModalLabelledBy: React.FunctionComponent<
  React.HTMLAttributes<HTMLHeadingElement>
> = props => (
  <h2 {...props} id={modalAriaLabelledById}>
    {props.children}
  </h2>
);

/**
 * A modal's description should be wrapped with this to support aria.
 * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
export const ModalDescribedBy: React.FunctionComponent<
  React.HTMLAttributes<HTMLDivElement>
> = props => (
  <div {...props} id={modalAriaDescribedById}>
    {props.children}
  </div>
);

/**
 * Used to identify container elements (react root elements),
 * where modals are rendered into with showModal().
 */
const modalContainerAttribute = 'data-modal-container';

/**
 * Renders the given modal to the document body.
 * This can be used to show a modal without having access to a react tree.
 */
export const showModal = (modal: React.ReactElement) => {
  // If an empty modal container exists, the modal will assume that it
  // should not use react portals, but is rendered into this dedicated container already.
  // This is unsafe and suboptimal, but since we cannot alter props of the modal here,
  // it is a working solution that comes with its own benefits.
  const container = document.createElement('div');
  container.setAttribute(modalContainerAttribute, 'true');
  container.setAttribute('data-testid', 'modal-container');
  document.body.appendChild(container);

  createRootAndRender(container, modal);
};

/**
 * Removes the container that wraps the given modal element.
 * The container was created by showModal().
 */
const maybeRemoveModalContainer = (modalBase: HTMLElement) => {
  const container = modalBase.parentElement;
  if (container?.hasAttribute(modalContainerAttribute)) {
    unmount(container);
    container.remove();
  }
};

/**
 * Some modals feature their own internal routing, which then needs a backwards navigation.
 * If given, a back button is rendered at the top of the modal. This will also disable the popStateInterceptor,
 * that would close the modal, in favour of the back button's popStateInterceptor.
 */
export type ModalBackButton = ModalBackButtonProps;

export type ModalProps = BlankModalProps & {
  /**
   * When passing JSX, please keep in mind to comply with the modal standards.
   */
  headline?: string | React.ReactNode;
  /**
   * Controls the X button in the corner.
   */
  noCloseButton?: boolean;
  backButton?: ModalBackButton;
};

export type ModalRef = BlankModalRef;

/**
 * A standard modal with a headline, a close button in the corner and primary + cancel buttons.
 */
export const Modal = React.forwardRef<ModalRef, React.PropsWithChildren<ModalProps>>(
  (props, ref) => {
    const modalRef = useRef<ModalRef>(null);
    const close = () => modalRef.current?.closeModal();

    return (
      <BlankModal
        {...props}
        className={props.className}
        containerClass={classList(props.containerClass)}
        ref={composeRefs(modalRef, ref)}
        disablePopStateInterceptor={!!props.backButton || props.disablePopStateInterceptor}
      >
        {!props.noCloseButton && <ModalCloseButton close={close} />}
        {props.headline && <ModalHeadline headline={props.headline} />}
        {props.backButton && <ModalBackButton {...props.backButton} />}
        {props.children}
      </BlankModal>
    );
  }
);

/**
 * Renders the X button in the corner of a modal.
 */
export const ModalCloseButton: React.FunctionComponent<{ close: () => void }> = props => (
  <div className="text-sk-grey-900 absolute top-4 right-4 flex items-center justify-center">
    <button onClick={props.close} aria-label="Schließen">
      <Icon name="close" className="text-2xl" />
    </button>
  </div>
);

/**
 * A modal headline needs the same id, that is used for aria-labelledby.
 */
export const ModalHeadline: React.FunctionComponent<{
  /**
   * When passing JSX, please keep in mind to comply with the modal standards.
   */
  headline: string | React.ReactNode;
  className?: string;
}> = props => (
  <ModalLabelledBy
    id={modalAriaLabelledById}
    className={classList('text-2xl/8 font-extrabold', props.className)}
  >
    {props.headline}
  </ModalLabelledBy>
);

/**
 * If the modal was created with showModal(), remove the container on close or unmount.
 */
const useModalContainer = (modalRef: React.RefObject<HTMLElement | null>, isOpen: boolean) => {
  // On close without unmount
  useEffect(() => {
    // requestAnimationFrame to wait until react has finished with this render cycle. Otherwise react will warn that we unmounted during rendering.
    requestAnimationFrame(() => {
      if (!isOpen && modalRef.current) {
        maybeRemoveModalContainer(modalRef.current);
      }
    });
  }, [modalRef, isOpen]);

  // On unmount
  useEffect(() => {
    const tempModalRef = modalRef;
    return () => {
      // requestAnimationFrame to wait until react has finished with this render cycle. Otherwise react will warn that we unmounted during rendering.
      requestAnimationFrame(() => {
        if (tempModalRef.current) {
          maybeRemoveModalContainer(tempModalRef.current);
        }
      });
    };
  }, [modalRef]);
};

/**
 * For some reason, smart browser engineers think that its a good idea, to propagate mousedown events on scrollbars to JS,
 * just as if the scrollbar wasn't there. This would lead our modal to close, if we didn't have this bit of logic.
 *
 * For further mysterious reasons, there is no browser API or dedicated way to detect, if a click was on a scrollbar.
 * So, our solution to the problem is, to declare the rightmost 25px of the modal backdrop (where the scrollbar is) as "safe zone".
 * Only on desktop though, as the tested mobile devices didn't have the problem or don't support dragging the scrollbar.
 */
const isBackdropScrollbarClick = (e: React.MouseEvent<HTMLElement>): boolean =>
  isMobile() ? false : e.clientX > e.currentTarget.offsetWidth - 25;

export type BlankModalProps = {
  defaultOpen?: boolean;
  className?: string;
  maxHeightClass?: string;
  containerClass?: string;
  onClose?: () => void;
  noCloseOnBackdropClick?: boolean;
  /**
   * Clicking on any link (primary button only) inside the modal closes it.
   */
  linksCloseModal?: boolean;
  /**
   * A popStateInterceptor, that closes the modal, is used by default.
   * Disabling it can be necessary however, if a specific modal features different views and requires a popStateInterceptor itself.
   */
  disablePopStateInterceptor?: boolean;
  /**
   * The dialog will take all the size that it gets on the screen. Margins and paddings are automatically considered and adaptive.
   * Keep in mind, that the dialog itself is not scrollable. So, when using this, you might want to add a scrollable container
   * to the content. The `adaptiveFlexContent` prop might be useful as well.
   */
  fixHeightToScreen?: boolean;
  /**
   * Makes the dialog a flexbox with flex-direction: column.
   * Using this, the content can adapt to the available space of the modal dialog. So, it probably
   * only makes sense on modals with a fixed height.
   * Default elements of the Modal, like headlines or illustrations, are also part of the flexbox.
   */
  adaptiveFlexContent?: boolean;
  /**
   * Settings this to false, can be useful to hide the transition from one modal to another.
   * Defaults to true.
   */
  openingAnimation?: boolean;
  /**
   * Can be used to hide the modal, even though it is still rendered into the DOM. Useful for SEO.
   */
  alwaysIncludeInDom?: boolean;
};

export type BlankModalRef = { closeModal: () => void; openModal: () => void; isModalOpen: boolean };

/**
 * A blank modal, packed with functionality.
 */
export const BlankModal = React.forwardRef<BlankModalRef, React.PropsWithChildren<BlankModalProps>>(
  (props, ref) => {
    const {
      openingAnimation = true,
      defaultOpen = true,
      alwaysIncludeInDom = false,
      maxHeightClass = props.fixHeightToScreen &&
        'max-h-[65rem]' /* this is most commonly wanted, as larger modals seem overwhelming. */
    } = props;

    const { isSsr } = useIsSsr();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const closeModal = () => setIsOpen(false);
    const openModal = () => setIsOpen(true);
    const modalRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      closeModal,
      openModal,
      isModalOpen: isOpen
    }));

    useOnClose(isOpen, props.onClose);
    useModalPopStateInterceptor(isOpen, closeModal, props.disablePopStateInterceptor);
    const focusTrap = useFocusTrap({ enabled: isOpen });
    useModalContainer(modalRef, isOpen);
    const dismissViaEscape = useDismissViaEscape({
      closeModal,
      enabled: !props.noCloseOnBackdropClick
    });

    const onBackdropMouseDown = (e: React.MouseEvent<HTMLElement>) => {
      if (!props.noCloseOnBackdropClick && !isBackdropScrollbarClick(e)) {
        closeModal();
      }
    };

    const onDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (props.linksCloseModal && (e.target as HTMLElement).closest('a')) {
        if (isPrimaryMouseButtonWithoutModifier(e)) {
          closeModal();
        }
      }
    };

    const adTabSetup = new Cookie('sk-AB-tabsetup').value ?? '0';

    const result =
      !isOpen && !alwaysIncludeInDom ? (
        <div ref={modalRef}></div>
      ) : (
        <>
          <BackdropScrollContainer
            className={classList(
              'z-(--z-index-modal) overflow-x-hidden text-center outline-none',
              openingAnimation && 'Modal--withOpeningAnimation',
              props.fixHeightToScreen && 'flex',
              props.containerClass,
              alwaysIncludeInDom && !isOpen && 'hidden'
            )}
            ref={composeRefs(modalRef, ref => focusTrap.onElementRef(ref))}
            onKeyDown={dismissViaEscape.onKeyDown}
          >
            <Backdrop
              // mousedown instead of click, because it often prevents accidental close of the modal
              // (when the user starts dragging from inside the modal and releases above the backdrop to e.g. select text)
              onMouseDown={onBackdropMouseDown}
              opacity={openingAnimation ? { type: 'fade-in' } : { type: 'fixed', value: 1 }}
              data-testid="modal-backdrop"
            />
            {/* Disabled eslint rules, because only clicks on links inside the dialog are enhanced with supplementary behaviour. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              {...ariaDialogPattern({
                modal: true,
                labelledBy: modalAriaLabelledById,
                describedBy: modalAriaDescribedById
              }).dialogProps}
              className={classList(
                'bg-sk-grey-0 relative z-(--z-index-modal-dialog) mt-3 mr-auto mb-3 ml-auto w-[calc(100%-4rem)] max-w-[400px] rounded-4xl p-4 pt-14 text-left',
                maxHeightClass,
                openingAnimation && 'animate-modalMoveUp',
                props.adaptiveFlexContent && 'flex flex-col',
                props.className
              )}
              onClick={onDialogClick}
            >
              {props.children}
            </div>
          </BackdropScrollContainer>
        </>
      );

    // If a dedicated container for this modal exists, don't use react portal.
    // A container is not passed via props, but searched accross the DOM, because showModal() cannot alter props.
    const dedicatedModalContainerExists: boolean = useMemo(
      () => !isSsr && !!document.querySelector(`[${modalContainerAttribute}]:empty`),
      []
    );

    return dedicatedModalContainerExists || isSsr
      ? result
      : ReactDOM.createPortal(result, document.body);
  }
);
